import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, off } from 'firebase/database';
import { managementDb } from '../firebase';
import './Home.css';

function Home() {
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAtkinsVideoModal, setShowAtkinsVideoModal] = useState(false);
  const [showCashfreeVideoModal, setShowCashfreeVideoModal] = useState(false);
  const [showTallyVideoModal, setShowTallyVideoModal] = useState(false);
  const [showGEVideoModal, setShowGEVideoModal] = useState(false);
  const [showDellVideoModal, setShowDellVideoModal] = useState(false);
  const [showHeroDetails, setShowHeroDetails] = useState(false);

  // Firebase video state
  const [activeOpeningVideo, setActiveOpeningVideo] = useState(null);
  const [activeFeaturedVideo, setActiveFeaturedVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Error boundary effect
  useEffect(() => {
    const handleError = (error) => {
      console.error('Home component error:', error);
      setHasError(true);
    };

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Fetch active opening video from Firebase
  useEffect(() => {
    if (!managementDb) {
      setLoading(false);
      return;
    }

    try {
      const openingVideosRef = ref(managementDb, 'home/opening-videos');

      const fetchActiveOpeningVideo = onValue(openingVideosRef, (snapshot) => {
        try {
          const data = snapshot.val();

          if (data) {
            const videosArray = Object.keys(data).map(key => ({
              id: key,
              ...data[key]
            }));
            const active = videosArray.find(video => video.isActive === true);
            setActiveOpeningVideo(active || null);
          } else {
            setActiveOpeningVideo(null);
          }
        } catch (error) {
          console.error('Error processing opening video data:', error);
          setActiveOpeningVideo(null);
        }
      }, (error) => {
        console.error('Error fetching active opening video:', error);
        setActiveOpeningVideo(null);
      });

      return () => {
        try {
          off(openingVideosRef, 'value', fetchActiveOpeningVideo);
        } catch (error) {
          console.error('Error cleaning up opening video listener:', error);
        }
      };
    } catch (error) {
      console.error('Error setting up opening video listener:', error);
      setActiveOpeningVideo(null);
    }
  }, []);

  // Scroll listener to show hero details
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setShowHeroDetails(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch active featured video from Firebase
  useEffect(() => {
    if (!managementDb) {
      setLoading(false);
      return;
    }

    try {
      const featuredVideosRef = ref(managementDb, 'home/featured-videos');

      const fetchActiveFeaturedVideo = onValue(featuredVideosRef, (snapshot) => {
        try {
          const data = snapshot.val();

          if (data) {
            const videosArray = Object.keys(data).map(key => ({
              id: key,
              ...data[key]
            }));
            const active = videosArray.find(video => video.isActive === true);
            setActiveFeaturedVideo(active || null);
          } else {
            setActiveFeaturedVideo(null);
          }
          setLoading(false);
        } catch (error) {
          console.error('Error processing featured video data:', error);
          setActiveFeaturedVideo(null);
          setLoading(false);
        }
      }, (error) => {
        console.error('Error fetching active featured video:', error);
        setActiveFeaturedVideo(null);
        setLoading(false);
      });

      return () => {
        try {
          off(featuredVideosRef, 'value', fetchActiveFeaturedVideo);
        } catch (error) {
          console.error('Error cleaning up featured video listener:', error);
        }
      };
    } catch (error) {
      console.error('Error setting up featured video listener:', error);
      setActiveFeaturedVideo(null);
      setLoading(false);
    }
  }, []);

  // If there's an error, show fallback UI
  if (hasError) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1E5EAB, #2a6bc7)',
        color: 'white',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <div>
          <h1>The Video Company</h1>
          <p>Something went wrong. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'white',
              color: '#1E5EAB',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Helper function to extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    try {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    } catch (error) {
      console.error('Error extracting YouTube video ID:', error);
      return null;
    }
  };

  const HERO_FALLBACK_VIDEO_ID = 'COB0b1avcs8';
  const FEATURED_FALLBACK_VIDEO_ID = '0KoK0A3FMr4';

  const servicesList = [
    {
      title: 'Video Production',
      description: 'Concept-to-delivery crews for every scale.',
      icon: 'video',
      id: 'video-production'
    },
    {
      title: 'Commercials',
      description: 'Campaign films engineered to convert.',
      icon: 'tv',
      id: 'commercial-filming'
    },
    {
      title: 'Corporate Films',
      description: 'Executive narratives and culture pieces.',
      icon: 'building',
      id: 'corporate-videos'
    },
    {
      title: 'Drone Filming',
      description: 'Certified aerial cinematography.',
      icon: 'helicopter',
      id: 'drone-filming'
    },
    {
      title: 'Post Production',
      description: 'Edit, grade, mix, master.',
      icon: 'film',
      id: 'post-production'
    },
    {
      title: 'Product Shoots',
      description: 'Launch-ready product stories.',
      icon: 'camera',
      id: 'product-shoots'
    },
    {
      title: 'Training Videos',
      description: 'Modular learning content for teams.',
      icon: 'chalkboard-teacher',
      id: 'training-videos'
    }
  ];

  const scrollToSection = (sectionId) => {
    const target = document.getElementById(sectionId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Gallery data
  const galleryItems = [
    {
      id: 1,
      category: 'commercial',
      imgUrl: 'https://i.postimg.cc/tTp3nSjD/5L1A1888.jpg',
      title: 'Product Commercial'
    },
    {
      id: 2,
      category: 'corporate',
      imgUrl: 'https://i.postimg.cc/brzWN9r2/2.jpg',
      title: 'Corporate Interview'
    },
    {
      id: 3,
      category: 'event',
      imgUrl: 'https://i.postimg.cc/523HGphh/Whats-App-Image-2026-03-30-at-12-46-02-PM.jpg',
      title: 'Corporate Event'
    },
    {
      id: 4,
      category: 'commercial',
      imgUrl: 'https://i.postimg.cc/151f3LsX/Whats-App-Image-2026-03-30-at-12-45-38-PM.jpg',
      title: 'Brand Campaign'
    },
    {
      id: 5,
      category: 'corporate',
      imgUrl: 'https://i.postimg.cc/prfVjLRW/Whats-App-Image-2026-03-30-at-12-45-39-PM.jpg',
      title: 'Company Culture'
    },
    {
      id: 6,
      category: 'event',
      imgUrl: 'https://i.postimg.cc/YSW50ytV/Whats-App-Image-2026-03-30-at-12-45-39-PM-(1).jpg',
      title: 'Product Launch'
    },
    {
      id: 7,
      category: 'commercial',
      imgUrl: 'https://i.postimg.cc/ZnKs8fh0/Whats-App-Image-2026-03-30-at-12-45-42-PM.jpg',
      title: 'TV Commercial'
    },
    {
      id: 8,
      category: 'corporate',
      imgUrl: 'https://i.postimg.cc/1XfcRbQJ/Whats-App-Image-2026-03-30-at-12-45-20-PM.jpg',
      title: 'Training Video'
    },
    {
      id: 9,
      category: 'event',
      imgUrl: 'https://i.postimg.cc/hPYZmbZP/Whats-App-Image-2026-03-30-at-12-46-02-PM-(1).jpg',
      title: 'Conference Coverage'
    }
  ];

  const filteredItems = galleryItems;
  const itemsToShow = showMore ? filteredItems : filteredItems.slice(0, 6);

  const openModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  const navigateImage = (direction) => {
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : filteredItems.length - 1;
    } else {
      newIndex = currentImageIndex < filteredItems.length - 1 ? currentImageIndex + 1 : 0;
    }
    setCurrentImageIndex(newIndex);
  };

  const heroVideoId = getYouTubeVideoId(activeOpeningVideo?.videoUrl) || HERO_FALLBACK_VIDEO_ID;
  const heroTitle = 'Where Vision Meets Cinematic Excellence';
  const heroDescription = 'From concept to delivery, we partner with brands to create premium video content that resonates across every platform.';
  const featuredVideoId = getYouTubeVideoId(activeFeaturedVideo?.videoUrl) || FEATURED_FALLBACK_VIDEO_ID;

  return (
    <div className="home-page">
      {/* Full-screen Image Modal */}
      {isModalOpen && (
        <div className="image-modal">
          <div className="modal-overlay" onClick={closeModal}></div>

          <button className="modal-close" onClick={closeModal}>
            <i className="fas fa-times"></i>
          </button>

          <img
            src={filteredItems[currentImageIndex].imgUrl}
            alt={filteredItems[currentImageIndex].title}
            className="modal-image"
          />

          <button
            className="modal-nav prev"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('prev');
            }}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <button
            className="modal-nav next"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('next');
            }}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Atkins Video Modal */}
      {showAtkinsVideoModal && (
        <div className="atkins-video-modal" style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <div style={{position: 'relative', width: '90vw', maxWidth: 700, background: '#000', borderRadius: 12, boxShadow: '0 4px 32px rgba(0,0,0,0.3)'}}>
            <button onClick={() => setShowAtkinsVideoModal(false)} style={{position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 22, cursor: 'pointer', zIndex: 2}}>&times;</button>
            <div style={{position: 'relative', paddingTop: '56.25%'}}>
              <iframe
                src="https://www.youtube.com/embed/dkMU1zzktdA?autoplay=1&controls=0"
                title="Atkins Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: 12, background: '#000', pointerEvents: 'none'}}
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Cashfree Video Modal */}
      {showCashfreeVideoModal && (
        <div className="cashfree-video-modal" style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <div style={{position: 'relative', width: '90vw', maxWidth: 700, background: '#000', borderRadius: 12, boxShadow: '0 4px 32px rgba(0,0,0,0.3)'}}>
            <button onClick={() => setShowCashfreeVideoModal(false)} style={{position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 22, cursor: 'pointer', zIndex: 2}}>&times;</button>
            <div style={{position: 'relative', paddingTop: '56.25%'}}>
              <iframe
                src="https://www.youtube.com/embed/8d1-lNNVwUU?si=8iFOKkWVp6aX1IJ4&autoplay=1&controls=0"
                title="Cashfree Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: 12, background: '#000', pointerEvents: 'none'}}
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Tally Video Modal */}
      {showTallyVideoModal && (
        <div className="tally-video-modal" style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <div style={{position: 'relative', width: '90vw', maxWidth: 700, background: '#000', borderRadius: 12, boxShadow: '0 4px 32px rgba(0,0,0,0.3)'}}>
            <button onClick={() => setShowTallyVideoModal(false)} style={{position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 22, cursor: 'pointer', zIndex: 2}}>&times;</button>
            <div style={{position: 'relative', paddingTop: '56.25%'}}>
              <iframe
                src="https://www.youtube.com/embed/COB0b1avcs8?autoplay=1&controls=0"
                title="Tally Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: 12, background: '#000', pointerEvents: 'none'}}
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* GE Video Modal */}
      {showGEVideoModal && (
        <div className="ge-video-modal" style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <div style={{position: 'relative', width: '90vw', maxWidth: 700, background: '#000', borderRadius: 12, boxShadow: '0 4px 32px rgba(0,0,0,0.3)'}}>
            <button onClick={() => setShowGEVideoModal(false)} style={{position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 22, cursor: 'pointer', zIndex: 2}}>&times;</button>
            <div style={{position: 'relative', paddingTop: '56.25%'}}>
              <iframe
                src="https://www.youtube.com/embed/jIWNb9-sfwk?autoplay=1&controls=0"
                title="GE Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: 12, background: '#000', pointerEvents: 'none'}}
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Dell Video Modal */}
      {showDellVideoModal && (
        <div className="dell-video-modal" style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <div style={{position: 'relative', width: '90vw', maxWidth: 700, background: '#000', borderRadius: 12, boxShadow: '0 4px 32px rgba(0,0,0,0.3)'}}>
            <button onClick={() => setShowDellVideoModal(false)} style={{position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 22, cursor: 'pointer', zIndex: 2}}>&times;</button>
            <div style={{position: 'relative', paddingTop: '56.25%'}}>
              <iframe
                src="https://www.youtube.com/embed/FwC8TptlU8g?si=zNq6C_F5Vo1f55td&autoplay=1&controls=0"
                title="Dell Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: 12, background: '#000', pointerEvents: 'none'}}
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* ─── Immersive Hero ─── */}
      <section className="hero-shell" id="home-hero">
        <div className="hero-media">
          {heroVideoId ? (
            <>
              <iframe
                src={`https://www.youtube.com/embed/${heroVideoId}?autoplay=1&mute=1&loop=1&controls=0&disablekb=1&fs=0&modestbranding=1&rel=0&iv_load_policy=3&showinfo=0&color=white&autohide=1&playlist=${heroVideoId}&playsinline=1`}
                title={heroTitle}
                allow="autoplay; encrypted-media"
                style={{ pointerEvents: 'none' }}
              />
              {/* Transparent overlay to block all YouTube UI interactions */}
              <div className="video-ui-blocker" />
            </>
          ) : (
            <div className="video-placeholder">
              {loading ? 'Loading video...' : 'No active video configured'}
            </div>
          )}
        </div>
        <div className="hero-overlay"></div>
      </section>

      {showHeroDetails && (
        <section className="hero-details">
          <div className="hero-details__inner">
            <div>
              <p className="hero-eyebrow">The Video Company</p>
              <h1>{heroTitle}</h1>
              <p className="hero-description">{heroDescription}</p>
            </div>
          </div>
        </section>
      )}

      {/* Page Content */}
      <div className="page-content">

        {/* ─── Featured Work ─── */}
        <section id="featured-work" className="featured-showreel">
          <div className="featured-showreel__media">
            <div className="featured-media-frame">
              <span className="featured-pill">Latest release</span>
              {featuredVideoId ? (
                <>
                  <iframe
                    src={`https://www.youtube.com/embed/${featuredVideoId}?autoplay=1&mute=1&loop=1&controls=0&disablekb=1&fs=0&modestbranding=1&rel=0&iv_load_policy=3&showinfo=0&color=white&autohide=1&playlist=${featuredVideoId}&playsinline=1`}
                    title={activeFeaturedVideo?.title || 'Featured Video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ pointerEvents: 'none' }}
                  />
                  {/* Transparent overlay to block all YouTube UI interactions */}
                  <div className="video-ui-blocker" />
                </>
              ) : (
                <div className="featured-fallback">
                  {loading ? 'Loading video...' : 'No active video configured'}
                </div>
              )}
            </div>
          </div>
          <div className="featured-showreel__details">
            <div className="featured-header">
              <p className="eyebrow">Latest release</p>
              <h3>{activeFeaturedVideo?.title || 'Showreel 2025'}</h3>
              <p>
                {activeFeaturedVideo?.description ||
                  'Experience how we orchestrate narrative, design, and sound to transform briefs into cinematic experiences.'}
              </p>
            </div>
            <div className="featured-actions">
              <button className="hero-btn primary" onClick={() => scrollToSection('services')}>
                View Services
              </button>
              {activeFeaturedVideo?.videoUrl && (
                <button
                  className="hero-btn ghost"
                  onClick={() => window.open(activeFeaturedVideo.videoUrl, '_blank')}
                >
                  Watch on YouTube
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="section-divider"></div>

        {/* ─── Services ─── */}
        <section id="services" className="services-section">
          <div className="container">
            <div className="section-header text-center mb-5">
              <h2 className="section-heading">Our Services</h2>
              <p className="section-subtitle">Explore our video production services</p>
            </div>
            <div className="services-grid">
              {servicesList.map((service, index) => (
                <button
                  key={service.title}
                  className="service-card"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => service.id && navigate(`/services?service=${service.id}`)}
                >
                  <span className="service-arrow">
                    <i className="fas fa-arrow-right"></i>
                  </span>
                  <div className="service-icon">
                    <i className={`fas fa-${service.icon}`}></i>
                  </div>
                  <div className="service-body">
                    <h4>{service.title}</h4>
                    <p>{service.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="section-divider"></div>

        {/* ─── Gallery ─── */}
        <section id="gallery" className="gallery-section">
          <div className="container">
            <div className="section-header text-center mb-5">
              <h2 className="section-heading">TVC Diaries</h2>
              <p className="section-subtitle">Explore our professional work on sets</p>
            </div>

            <div className="gallery-grid">
              {itemsToShow.map((item, index) => (
                <div
                  key={item.id}
                  className={`gallery-item ${index === 0 ? 'gallery-item--hero' : ''} ${index % 4 === 3 ? 'gallery-item--tall' : ''}`}
                  onClick={() => openModal(filteredItems.indexOf(item))}
                >
                  <img
                    src={item.imgUrl}
                    alt={item.title}
                    className="gallery-img"
                    loading="lazy"
                  />
                  <div className="gallery-overlay">
                    <span>{item.category}</span>
                    <h3>{item.title}</h3>
                  </div>
                </div>
              ))}
            </div>

            <div className="gallery-actions-container">
              {filteredItems.length > 6 && (
                <div className="show-more-wrapper">
                  {!showMore ? (
                    <button className="show-more-btn" onClick={() => setShowMore(true)}>
                      Show More ({filteredItems.length - 6})
                    </button>
                  ) : (
                    <button
                      className="show-less-btn"
                      onClick={() => {
                        setShowMore(false);
                        document.getElementById('gallery').scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      Show Less
                    </button>
                  )}
                </div>
              )}

              <div className="explore-works-wrapper">
                <button className="explore-works-btn" onClick={() => navigate('/gallery')}>
                  Explore Our Works <i className="fas fa-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="section-divider"></div>

        {/* ─── Clients ─── */}
        <section id="clients" className="clients-section">
          <div className="container-fluid p-0">
            <div className="section-header text-center mb-5">
              <h2 className="section-heading">Trusted by Industry Leaders</h2>
              <p className="section-subtitle">We partner with innovative companies across the globe</p>
            </div>

            <div className="scrolling-rows-container">
              {/* Top Row (scrolls left) */}
              <div className="logo-row top-row">
                <div className="logo-track">
                  {["Atkins-01", "accenture-01", "woodenstreets", "datamatics", "cashfree", "amazon", "microsoft", "fastrack", "vymo", "Dell"].map((logo, index) => (
                    <div className="logo-item" key={`top-${index}`}>
                      {logo === "Atkins-01" ? (
                        <img
                          src={require(`../assets/logos/${logo}.svg`)}
                          alt={logo.replace('-01', '')}
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowAtkinsVideoModal(true)}
                        />
                      ) : logo === "cashfree" ? (
                        <img
                          src={require(`../assets/logos/${logo}.svg`)}
                          alt={logo}
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowCashfreeVideoModal(true)}
                        />
                      ) : logo === "Dell" ? (
                        <img
                          src={require(`../assets/logos/${logo}.svg`)}
                          alt={logo}
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowDellVideoModal(true)}
                        />
                      ) : (
                        <img src={require(`../assets/logos/${logo}.svg`)} alt={logo.replace('-01', '')} />
                      )}
                    </div>
                  ))}
                  {/* Mirrored set for seamless looping */}
                  {["Atkins-01", "accenture-01", "woodenstreets", "datamatics", "cashfree", "amazon", "microsoft", "fastrack", "vymo"].map((logo, index) => (
                    <div className="logo-item" key={`top-mirror-${index}`}>
                      {logo === "Atkins-01" ? (
                        <img
                          src={require(`../assets/logos/${logo}.svg`)}
                          alt={logo.replace('-01', '')}
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowAtkinsVideoModal(true)}
                        />
                      ) : logo === "cashfree" ? (
                        <img
                          src={require(`../assets/logos/${logo}.svg`)}
                          alt={logo}
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowCashfreeVideoModal(true)}
                        />
                      ) : (
                        <img src={require(`../assets/logos/${logo}.svg`)} alt={logo.replace('-01', '')} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Row (scrolls right) */}
              <div className="logo-row bottom-row">
                <div className="logo-track">
                  {['hdfc', 'tally', 'tanishq', 'britannia', 'ge', 'metricstream', 'shipsy', 'asense'].map((logo, index) => (
                    <div className="logo-item" key={`bottom-${index}`}>
                      {logo === "tally" ? (
                        <img
                          src={require(`../assets/logos/${logo}.svg`)}
                          alt={logo}
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowTallyVideoModal(true)}
                        />
                      ) : (
                        <img src={require(`../assets/logos/${logo}.svg`)} alt={logo} />
                      )}
                    </div>
                  ))}
                  {/* Mirrored set for seamless looping */}
                  {['hdfc', 'tally', 'tanishq', 'britannia', 'ge', 'metricstream', 'shipsy', 'asense'].map((logo, index) => (
                    <div className="logo-item" key={`bottom-mirror-${index}`}>
                      {logo === "ge" ? (
                        <img
                          src={require(`../assets/logos/${logo}.svg`)}
                          alt={logo}
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowGEVideoModal(true)}
                        />
                      ) : logo === "tally" ? (
                        <img
                          src={require(`../assets/logos/${logo}.svg`)}
                          alt={logo}
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowTallyVideoModal(true)}
                        />
                      ) : (
                        <img src={require(`../assets/logos/${logo}.svg`)} alt={logo} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Spacer */}
        <div className="footer-spacer"></div>
      </div>
    </div>
  );
}

export default Home;